import { Box, MenuItem, TextField, Tooltip } from "@mui/material";

/**
 * Three "insert a clause" dropdowns above the condition textarea. These don't
 * bind to real data — a condition is evaluated by the LLM against whatever
 * meeting/client context is available at run time, so a dropdown here just
 * drops in a well-formed phrase the evaluator can act on, rather than making
 * the adviser compose free text from scratch every time. Picking a value
 * always appends (never replaces) the current condition text.
 */

const QUICK_CONDITIONS = [
  { label: "Last meeting before retirement", text: "If this was the client's last scheduled meeting before retirement, take Yes; otherwise take No." },
  { label: "Client mentioned a life event", text: "If the client mentioned a major life event (marriage, new child, job change, inheritance), take Yes; otherwise take No." },
  { label: "Risk profile discussed", text: "If the client's risk tolerance or investment risk profile was discussed or questioned in this meeting, take Yes; otherwise take No." },
  { label: "Compliance concern raised", text: "If the client raised a complaint or a compliance-sensitive concern in this meeting, take Yes; otherwise take No." },
  { label: "Follow-up meeting requested", text: "If the client asked to book a follow-up meeting, take Yes; otherwise take No." },
];

const MEETING_TYPES = [
  "Annual Review",
  "Initial Consultation",
  "Progress Review",
  "Retirement Planning",
  "Insurance Review",
  "Estate Planning",
  "Ad-hoc Catch-up",
];

const EMAIL_TYPES = [
  { value: "meeting_summary", label: "Meeting summary" },
  { value: "assets_liabilities_request", label: "Assets & liabilities request" },
  { value: "cashflow_followup", label: "Cash flow follow-up" },
  { value: "client_followup", label: "General client follow-up" },
  { value: "document_request", label: "Document request" },
];

function append(current: string, clause: string): string {
  const trimmed = current.trim();
  return trimmed ? `${trimmed} ${clause}` : clause;
}

export default function ConditionQuickInsert({
  value,
  onInsert,
}: {
  value: string;
  onInsert: (next: string) => void;
}) {
  return (
    <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
      <Tooltip title="Insert a ready-made condition">
        <TextField
          select
          size="small"
          label="Quick condition"
          value=""
          sx={{ minWidth: 160 }}
          onChange={(e) => {
            const found = QUICK_CONDITIONS.find((q) => q.label === e.target.value);
            if (found) onInsert(found.text);
          }}
        >
          {QUICK_CONDITIONS.map((q) => (
            <MenuItem key={q.label} value={q.label}>
              {q.label}
            </MenuItem>
          ))}
        </TextField>
      </Tooltip>

      <Tooltip title="Insert a clause about the meeting type">
        <TextField
          select
          size="small"
          label="Meeting type is…"
          value=""
          sx={{ minWidth: 150 }}
          onChange={(e) => {
            if (e.target.value) onInsert(append(value, `The meeting type is "${e.target.value}".`));
          }}
        >
          {MEETING_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </TextField>
      </Tooltip>

      <Tooltip title="Insert a clause about the email being drafted downstream">
        <TextField
          select
          size="small"
          label="Email type is…"
          value=""
          sx={{ minWidth: 150 }}
          onChange={(e) => {
            const found = EMAIL_TYPES.find((t) => t.value === e.target.value);
            if (found) onInsert(append(value, `The email being drafted after this step is a "${found.label}".`));
          }}
        >
          {EMAIL_TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
      </Tooltip>
    </Box>
  );
}
